import { STAGES } from "./stages.js";
import { compareByUrgency } from "./urgency.js";
/**
 * Zone geometry, in world units. Sticky positions are never stored — they are
 * recomputed from this on every render, so the board cannot drift out of sync
 * and two people dragging at once can never fight over coordinates.
 *
 * A client is a column on the whiteboard. Its stages are bands stacked down
 * that column, in STAGES order, separated by a drawn marker stroke. Notes flow
 * left to right across the column's full width and wrap, and a band grows
 * downward as far as it needs — everything below it moves down to make room.
 */
export const LAYOUT = {
    gridSnap: 40,
    zone: {
        defaultWidth: 600,
        defaultHeight: 900,
        minWidth: 380,
        maxWidth: 1600,
        minHeight: 320,
        maxHeight: 8000,
        padding: 28,
        headerHeight: 72,
    },
    band: {
        /**
         * Vertical space between two bands. The separator stroke is drawn into the
         * middle of it, so half of this is the clearance between the ink and the
         * notes on either side — a stroke that nearly touches the notes reads as
         * an underline on the row above rather than a division between two.
         */
        gap: 64,
        /** An empty band is still somewhere you can put a note, so it keeps a floor. */
        minHeight: 96,
    },
    card: {
        // Square, because a real sticky note is.
        width: 168,
        height: 168,
        gap: 18,
    },
};
/** Usable width inside a zone, once its padding is taken off. */
export function contentWidthFor(zoneWidth) {
    return Math.max(LAYOUT.card.width, zoneWidth - LAYOUT.zone.padding * 2);
}
export function cardsPerRow(contentWidth) {
    const { width, gap } = LAYOUT.card;
    return Math.max(1, Math.floor((contentWidth + gap) / (width + gap)));
}
/**
 * Positions every sticky in a zone: four stage bands down the column, most
 * urgent first within each band. Pure, deterministic, and shared with auto-tidy.
 */
export function layoutZone(zoneWidth, tasks, now = new Date(), options = {}) {
    const { padding, headerHeight } = LAYOUT.zone;
    const contentWidth = contentWidthFor(zoneWidth);
    const perRow = cardsPerRow(contentWidth);
    const bands = [];
    const cards = [];
    const separators = [];
    let y = headerHeight;
    STAGES.forEach((stage, index) => {
        const inBand = tasks
            .filter((t) => t.stage === stage)
            .sort((a, b) => compareByUrgency(a, b, now));
        const visible = stage === "completed" && options.collapseCompleted && inBand.length > 1
            ? inBand.slice(0, 1)
            : inBand;
        visible.forEach((task, i) => {
            const row = Math.floor(i / perRow);
            const col = i % perRow;
            cards.push({
                taskId: task.id,
                stage,
                x: padding + col * (LAYOUT.card.width + LAYOUT.card.gap),
                y: y + row * (LAYOUT.card.height + LAYOUT.card.gap),
                width: LAYOUT.card.width,
                height: LAYOUT.card.height,
            });
        });
        const rows = Math.ceil(visible.length / perRow);
        const content = rows === 0 ? 0 : rows * (LAYOUT.card.height + LAYOUT.card.gap) - LAYOUT.card.gap;
        const height = Math.max(content, LAYOUT.band.minHeight);
        bands.push({ stage, x: padding, y, width: contentWidth, height });
        y += height;
        // The stroke sits in the middle of the gap, so the bands either side of it
        // are equally clear of the ink.
        if (index < STAGES.length - 1) {
            separators.push(y + LAYOUT.band.gap / 2);
            y += LAYOUT.band.gap;
        }
    });
    return {
        bands,
        cards,
        separators,
        requiredHeight: clamp(y + padding, LAYOUT.zone.minHeight, LAYOUT.zone.maxHeight),
    };
}
export function snapToGrid(value, grid = LAYOUT.gridSnap) {
    return Math.round(value / grid) * grid;
}
export function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
/** Resize from one corner while keeping the opposite corner fixed. */
export function resizeZoneFromCorner(zone, corner, dx, dy, minHeight = LAYOUT.zone.minHeight) {
    const west = corner.endsWith("w");
    const north = corner.startsWith("n");
    const width = clamp(zone.width + (west ? -dx : dx), LAYOUT.zone.minWidth, LAYOUT.zone.maxWidth);
    const height = clamp(zone.height + (north ? -dy : dy), minHeight, LAYOUT.zone.maxHeight);
    return {
        canvas_x: west ? zone.canvas_x + zone.width - width : zone.canvas_x,
        canvas_y: north ? zone.canvas_y + zone.height - height : zone.canvas_y,
        width,
        height,
    };
}
/** Snap a finished resize without moving the corner that was held fixed. */
export function snapZoneResize(zone, corner, minHeight = LAYOUT.zone.minHeight) {
    const west = corner.endsWith("w");
    const north = corner.startsWith("n");
    const width = clamp(snapToGrid(zone.width), LAYOUT.zone.minWidth, LAYOUT.zone.maxWidth);
    const height = clamp(snapToGrid(zone.height), minHeight, LAYOUT.zone.maxHeight);
    return {
        canvas_x: west ? zone.canvas_x + zone.width - width : zone.canvas_x,
        canvas_y: north ? zone.canvas_y + zone.height - height : zone.canvas_y,
        width,
        height,
    };
}
/**
 * Auto-tidy: keep the zone's width, shrink-wrap its height to exactly fit the
 * stickies, and snap the zone back onto the grid. One UPDATE on clients, no
 * per-task writes.
 */
export function tidyZone(zone, tasks, now = new Date()) {
    const width = clamp(snapToGrid(zone.width), LAYOUT.zone.minWidth, LAYOUT.zone.maxWidth);
    const { requiredHeight } = layoutZone(width, tasks, now);
    return {
        canvas_x: snapToGrid(zone.canvas_x),
        canvas_y: snapToGrid(zone.canvas_y),
        width,
        height: clamp(snapToGrid(requiredHeight), LAYOUT.zone.minHeight, LAYOUT.zone.maxHeight),
    };
}
/** Smallest height that will not clip current contents — the floor for manual resize. */
export function minHeightFor(zoneWidth, tasks, now = new Date()) {
    return layoutZone(zoneWidth, tasks, now).requiredHeight;
}
/**
 * Next free slot in an auto-expanding row of columns, so the board grows
 * sideways as clients are added — which is what the whiteboard actually looks
 * like: one client next to the next, all sharing the same surface.
 */
/** The clear space left between one client column and the next. */
export const ZONE_GAP = 80;
/**
 * Where a new client column goes: past the right edge of every existing one,
 * on the same row as the topmost.
 *
 * This used to be the first free slot in a five-wide grid, which meant a new
 * client could appear in a gap somewhere in the middle — wherever a column had
 * been deleted or dragged away from. Adding a client is a deliberate act and
 * you want to look straight at the result, so the board grows sideways and the
 * newest column is always the last one.
 *
 * `width` is optional so callers that only have positions still work; a column
 * of unknown width is assumed to be the default one.
 */
export function nextZoneSlot(existing) {
    if (existing.length === 0)
        return { canvas_x: 0, canvas_y: 0 };
    const right = Math.max(...existing.map((z) => z.canvas_x + (z.width ?? LAYOUT.zone.defaultWidth)));
    const top = Math.min(...existing.map((z) => z.canvas_y));
    return { canvas_x: snapToGrid(right + ZONE_GAP), canvas_y: snapToGrid(top) };
}
/** Anchored position -> where to draw it. A null anchor is already absolute. */
export function toWorld(anchor, local) {
    if (!anchor)
        return { x: local.x, y: local.y };
    return { x: anchor.x + local.x, y: anchor.y + local.y };
}
/** Where it was put -> what to store against that anchor. */
export function toAnchored(anchor, world) {
    if (!anchor)
        return { x: world.x, y: world.y };
    return { x: world.x - anchor.x, y: world.y - anchor.y };
}
//# sourceMappingURL=layout.js.map